import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Icon, Input, SkeletonText, useToast } from "@astrum/ui";
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_DESCRIPTION,
  NOTIFICATION_CATEGORY_LABEL,
  type NotificationCategoryPreferences,
} from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { brandInitials, useBranding } from "../lib/branding.js";
import "./workspace-settings.css";

/**
 * Workspace settings.
 *
 * Deliberately short. Everything operational — secrets, ports, upload
 * ceilings, retention — lives in the environment, where a bad value stops the
 * API from starting rather than being saved through a form. What is here is
 * what an administrator can reasonably change at runtime, and what the
 * application demonstrably reads.
 */

type Holiday = { date: string; label?: string };

type Settings = {
  workspaceName: string;
  workspaceLogo: string | null;
  invitationTtlHours: number;
  notificationCategories: NotificationCategoryPreferences;
  holidays: Holiday[];
  digestHourLocal: number | null;
  checkinReminderMinuteOfDayLocal: number | null;
};

/** The built-in defaults, mirrored from the server, shown as the field's
 *  placeholder so "unset" reads as a concrete time rather than a blank. */
const DEFAULT_DIGEST_HOUR = 7;
const DEFAULT_CHECKIN_REMINDER_MINUTE_OF_DAY = 16 * 60 + 30;

function minutesToClock(minutes: number): string {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function clockToMinutes(clock: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

/** Matches the server's cap. Kept a little under it so a borderline image is
 * rejected here, with an explanation, rather than as a 400 from the API. */
const MAX_LOGO_BYTES = 180_000;

const ACCEPTED = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];

/** Reads a file as a data URI — see the note in `settings.ts` on why the logo
 * is stored inline rather than in object storage. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.readAsDataURL(file);
  });
}

export function WorkspaceSettings() {
  const { toast } = useToast();
  const { refresh } = useBranding();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState<string | null>(null);
  const [ttl, setTtl] = useState("24");
  const [categories, setCategories] = useState<NotificationCategoryPreferences | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [newHolidayDate, setNewHolidayDate] = useState("");
  const [newHolidayLabel, setNewHolidayLabel] = useState("");
  const [digestClock, setDigestClock] = useState("");
  const [checkinClock, setCheckinClock] = useState("");
  /** The last saved state, for deciding whether Save should be enabled. */
  const [saved, setSaved] = useState<Settings | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { settings } = await api.get<{ settings: Settings }>("/admin/settings");
      setName(settings.workspaceName);
      setLogo(settings.workspaceLogo);
      setTtl(String(settings.invitationTtlHours));
      setCategories(settings.notificationCategories);
      setHolidays(settings.holidays);
      setDigestClock(
        settings.digestHourLocal === null ? "" : minutesToClock(settings.digestHourLocal * 60),
      );
      setCheckinClock(
        settings.checkinReminderMinuteOfDayLocal === null
          ? ""
          : minutesToClock(settings.checkinReminderMinuteOfDayLocal),
      );
      setSaved(settings);
    } catch {
      toast("Could not load settings.", { tone: "danger" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const ttlNumber = Number(ttl);
  const ttlValid = Number.isInteger(ttlNumber) && ttlNumber >= 1 && ttlNumber <= 168;
  const nameValid = name.trim().length > 0 && name.trim().length <= 60;

  const digestMinutes = digestClock.trim() === "" ? null : clockToMinutes(digestClock.trim());
  const digestValid = digestClock.trim() === "" || digestMinutes !== null;
  const checkinMinutes = checkinClock.trim() === "" ? null : clockToMinutes(checkinClock.trim());
  const checkinValid = checkinClock.trim() === "" || checkinMinutes !== null;

  const dirty =
    saved !== null &&
    categories !== null &&
    (name.trim() !== saved.workspaceName ||
      logo !== saved.workspaceLogo ||
      ttlNumber !== saved.invitationTtlHours ||
      JSON.stringify(categories) !== JSON.stringify(saved.notificationCategories) ||
      JSON.stringify(holidays) !== JSON.stringify(saved.holidays) ||
      (digestValid && (digestMinutes === null ? null : Math.floor(digestMinutes / 60)) !== saved.digestHourLocal) ||
      (checkinValid && checkinMinutes !== saved.checkinReminderMinuteOfDayLocal));

  const addHoliday = useCallback(() => {
    const date = newHolidayDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    const label = newHolidayLabel.trim();
    setHolidays((prev) => {
      if (prev.some((h) => h.date === date)) return prev;
      return [...prev, label ? { date, label } : { date }].sort((a, b) => a.date.localeCompare(b.date));
    });
    setNewHolidayDate("");
    setNewHolidayLabel("");
  }, [newHolidayDate, newHolidayLabel]);

  const removeHoliday = useCallback((date: string) => {
    setHolidays((prev) => prev.filter((h) => h.date !== date));
  }, []);

  const pickLogo = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      if (!ACCEPTED.includes(file.type)) {
        toast("Use a PNG, JPEG, SVG or WebP image.", { tone: "danger" });
        return;
      }
      if (file.size > MAX_LOGO_BYTES) {
        toast("That image is too large — keep it under 180 KB.", { tone: "danger" });
        return;
      }
      try {
        setLogo(await readAsDataUrl(file));
      } catch {
        toast("That file could not be read.", { tone: "danger" });
      }
    },
    [toast],
  );

  const save = useCallback(async () => {
    if (!nameValid || !ttlValid || !categories || !digestValid || !checkinValid) return;
    setSaving(true);
    try {
      const { settings } = await api.put<{ settings: Settings }>("/admin/settings", {
        workspaceName: name.trim(),
        workspaceLogo: logo,
        invitationTtlHours: ttlNumber,
        notificationCategories: categories,
        holidays,
        digestHourLocal: digestMinutes === null ? null : Math.floor(digestMinutes / 60),
        checkinReminderMinuteOfDayLocal: checkinMinutes,
      });
      setSaved(settings);
      setName(settings.workspaceName);
      setLogo(settings.workspaceLogo);
      setTtl(String(settings.invitationTtlHours));
      setCategories(settings.notificationCategories);
      setHolidays(settings.holidays);
      setDigestClock(
        settings.digestHourLocal === null ? "" : minutesToClock(settings.digestHourLocal * 60),
      );
      setCheckinClock(
        settings.checkinReminderMinuteOfDayLocal === null
          ? ""
          : minutesToClock(settings.checkinReminderMinuteOfDayLocal),
      );
      // The rail and the tab title read from the branding context, so it has
      // to be told rather than waiting for the next full load.
      await refresh();
      toast("Settings saved.", { tone: "ok" });
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not save settings.", { tone: "danger" });
    } finally {
      setSaving(false);
    }
  }, [
    name,
    logo,
    ttlNumber,
    categories,
    holidays,
    digestMinutes,
    checkinMinutes,
    nameValid,
    ttlValid,
    digestValid,
    checkinValid,
    refresh,
    toast,
  ]);

  if (loading) {
    return (
      <div className="wsettings">
        <SkeletonText lines={6} />
      </div>
    );
  }

  return (
    <div className="wsettings">
      <section className="wsettings__section">
        <div className="wsettings__section-head">
          <h3>Identity</h3>
          <p>How this workspace appears in the sidebar, on the sign-in screen, and in the browser tab.</p>
        </div>

        <div className="wsettings__brand-row">
          <div className="wsettings__logo-preview" aria-hidden="true">
            {logo ? <img src={logo} alt="" /> : <span>{brandInitials(name || "Astrum Connect")}</span>}
          </div>

          <div className="wsettings__logo-actions">
            <span className="wsettings__logo-label">Logo</span>
            <p className="wsettings__hint">
              {logo
                ? "Shown whole, never cropped. Square images sit best in the sidebar."
                : "No logo set. The initials above are used instead."}
            </p>
            <div className="wsettings__logo-buttons">
              <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
                {logo ? "Replace" : "Upload"}
              </Button>
              {logo && (
                <Button variant="ghost" size="sm" onClick={() => setLogo(null)}>
                  Remove
                </Button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED.join(",")}
              hidden
              onChange={(e) => {
                void pickLogo(e.target.files?.[0]);
                // Cleared so choosing the same file twice fires onChange again.
                e.target.value = "";
              }}
            />
          </div>
        </div>

        <Input
          label="Workspace name"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          error={name.length > 0 && !nameValid ? "Between 1 and 60 characters." : undefined}
        />
      </section>

      <section className="wsettings__section">
        <div className="wsettings__section-head">
          <h3>Invitations</h3>
          <p>
            An invitation link creates an account, so it is worth keeping the window short. Existing links are not
            affected by a change here — only ones issued afterwards.
          </p>
        </div>

        <Input
          label="Expire after (hours)"
          type="number"
          min={1}
          max={168}
          value={ttl}
          onChange={(e) => setTtl(e.target.value)}
          error={ttl.length > 0 && !ttlValid ? "Between 1 and 168 hours." : undefined}
        />
      </section>

      <section className="wsettings__section">
        <div className="wsettings__section-head">
          <h3>Holidays</h3>
          <p>
            Dates nobody is expected to check in or file a daily report, on top of the usual Saturday and Sunday
            weekend. Applies workspace-wide.
          </p>
        </div>

        <div className="wsettings__holiday-add">
          <Input
            label="Date"
            type="date"
            value={newHolidayDate}
            onChange={(e) => setNewHolidayDate(e.target.value)}
          />
          <Input
            label="Label (optional)"
            placeholder="e.g. Company holiday"
            maxLength={80}
            value={newHolidayLabel}
            onChange={(e) => setNewHolidayLabel(e.target.value)}
          />
          <Button variant="secondary" size="sm" onClick={addHoliday} disabled={!newHolidayDate.trim()}>
            Add
          </Button>
        </div>

        {holidays.length === 0 ? (
          <p className="wsettings__hint">No holidays configured.</p>
        ) : (
          <ul className="wsettings__holiday-list">
            {holidays.map((holiday) => (
              <li key={holiday.date} className="wsettings__holiday-row">
                <span className="wsettings__holiday-date">{holiday.date}</span>
                <span className="wsettings__holiday-label">{holiday.label ?? ""}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeHoliday(holiday.date)}
                  aria-label={`Remove ${holiday.date}`}
                >
                  <Icon name="trash" size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="wsettings__section">
        <div className="wsettings__section-head">
          <h3>Reminder times</h3>
          <p>
            When the daily digest and the "you haven't checked in" nudge go out, in each person's own local time.
            Leave blank to use the built-in default.
          </p>
        </div>

        <div className="wsettings__reminder-times">
          <Input
            label="Daily digest"
            type="time"
            placeholder={minutesToClock(DEFAULT_DIGEST_HOUR * 60)}
            value={digestClock}
            onChange={(e) => setDigestClock(e.target.value)}
            error={!digestValid ? "Use a valid time." : undefined}
          />
          <Input
            label="Check-in reminder"
            type="time"
            placeholder={minutesToClock(DEFAULT_CHECKIN_REMINDER_MINUTE_OF_DAY)}
            value={checkinClock}
            onChange={(e) => setCheckinClock(e.target.value)}
            error={!checkinValid ? "Use a valid time." : undefined}
          />
        </div>
      </section>

      <section className="wsettings__section">
        <div className="wsettings__section-head">
          <h3>Notifications</h3>
          <p>
            The master switch per kind of notification, for the whole workspace. A category turned off here cannot be
            turned back on by an individual — their own notification settings, in their Profile, only choose within
            what is left on here. Whether mail can send at all — the server address, the sender, and everything else
            it takes to actually deliver a message — is a deployment fact set in the environment, not here; see the
            mail status below for what is currently configured. Invitations and the "send a test to myself" button
            are not affected by anything on this screen — those are one-off sends, not a recurring category.
          </p>
        </div>

        {categories && (
          <div className="wsettings__categories" role="table" aria-label="Notification categories">
            <div className="wsettings__categories-row wsettings__categories-row--head" role="row">
              <span role="columnheader">Category</span>
              <span role="columnheader">In-app</span>
              <span role="columnheader">Email</span>
            </div>
            {NOTIFICATION_CATEGORIES.map((category) => (
              <div key={category} className="wsettings__categories-row" role="row">
                <div role="cell">
                  <span className="wsettings__categories-label">{NOTIFICATION_CATEGORY_LABEL[category]}</span>
                  <span className="wsettings__categories-desc">{NOTIFICATION_CATEGORY_DESCRIPTION[category]}</span>
                </div>
                <div role="cell">
                  <Checkbox
                    aria-label={`${NOTIFICATION_CATEGORY_LABEL[category]} — in-app`}
                    checked={categories[category].inApp}
                    onChange={(e) =>
                      setCategories((prev) =>
                        prev ? { ...prev, [category]: { ...prev[category], inApp: e.target.checked } } : prev,
                      )
                    }
                  />
                </div>
                <div role="cell">
                  <Checkbox
                    aria-label={`${NOTIFICATION_CATEGORY_LABEL[category]} — email`}
                    checked={categories[category].email}
                    onChange={(e) =>
                      setCategories((prev) =>
                        prev ? { ...prev, [category]: { ...prev[category], email: e.target.checked } } : prev,
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="wsettings__actions">
        {dirty && (
          <span className="wsettings__unsaved">
            <Icon name="warning" size={14} />
            Unsaved changes
          </span>
        )}
        <Button variant="ghost" onClick={() => void load()} disabled={!dirty || saving}>
          Discard
        </Button>
        <Button
          onClick={() => void save()}
          loading={saving}
          disabled={!dirty || !nameValid || !ttlValid || !digestValid || !checkinValid}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}
